import type { MDXProps } from "mdx/types.js";
import type { JSX } from "react";
import type { ZudokuDocsConfig } from "../../../config/validators/ZudokuConfig.js";
import type { Toc } from "../../../vite/mdx/rehype-extract-toc-with-jsx.js";
import type { ZudokuPlugin } from "../../core/plugins.js";

export interface MarkdownPluginOptions extends Pick<
  ZudokuDocsConfig,
  "defaultOptions" | "publishMarkdown"
> {
  basePath: string;
  fileImports: Record<string, () => Promise<MDXImport>>;
}
export type MarkdownPluginDefaultOptions = Pick<
  Frontmatter,
  | "toc"
  | "disablePager"
  | "showLastModified"
  | "suggestEdit"
  | "copyPage"
  | "fullWidth"
  | "centered"
>;

export type Frontmatter = {
  title?: string;
  description?: string;
  // Overrides the browser title and meta description only
  seo?: { title?: string; description?: string };
  category?: string;
  draft?: boolean;
  toc?: boolean;
  disablePager?: boolean;
  disable_pager?: boolean;
  showLastModified?: boolean;
  lastModifiedTime?: number;
  suggestEdit?: { url: string; text?: string } | false;
  copyPage?: boolean;
  fullWidth?: boolean;
  centered?: boolean;
};

export type MDXImport = {
  tableOfContents: Toc;
  frontmatter: Frontmatter;
  excerpt?: string;
  __filepath: string;
  default: (props: MDXProps) => JSX.Element;
};

export const markdownPlugin = (
  options: MarkdownPluginOptions,
): ZudokuPlugin => ({
  getRoutes: () => {
    return Object.entries(options.fileImports).map(
      ([routePath, importPromise]) => ({
        path: routePath,
        lazy: async () => {
          const { MdxPage } = await import("./MdxPage.js");
          const { default: Component, ...props } = await importPromise();
          return {
            element: (
              <MdxPage
                basePath={options.basePath}
                mdxComponent={Component}
                {...props}
                defaultOptions={options.defaultOptions}
                publishMarkdown={options.publishMarkdown}
              />
            ),
          };
        },
      }),
    );
  },
});
